import { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import { useStore } from '@/redux/store';
import { AuthAPI } from '@/services/api/auth';
import {
  clearSession,
  saveAuthType,
} from '@/services/localService/localService';
import axios from 'axios';
import {
  LocalStorageService,
  maskNRIC,
  reportDtCustomError,
  useHTTPError,
} from 'br2s-ui-common-utils-02';
import { checkIndicator } from '@/utilities/utilities';
import { toggleEntityChange } from '../ProfileSelection/profileSelection-utilities';
import { flushSync } from 'react-dom';

const Authenticate = () => {
  const { toggleLoading, authenticate } = useStore();
  const { handleHTTPError } = useHTTPError();
  const { search } = useLocation();
  const { loginType } = useParams();
  const navigate = useNavigate();
  const params = new URLSearchParams(search);
  const [, setCookie] = useCookies(['sessionType', 'isSessionActive']);

  useEffect(() => {
    if (params.has('state') && params.has('code') && document.referrer !== '') {
      toggleLoading(true);
      handleGetToken();
      return;
    }

    clearSession();
    navigate('/');
  }, []);

  // istanbul ignore next
  const handleGetToken = async () => {
    try {
      const response = await AuthAPI.getToken({
        state: params.get('state') as string,
        code: params.get('code') as string,
      });

      if (response?.status === 200) {
        handleAuthScope();
      }
    } catch (error) {
      console.error(error);
      // istanbul ignore next
      if (axios.isAxiosError(error)) {
        reportDtCustomError(
          'AuthFailureError',
          'Token API failed.',
          `${error.response?.status}: ${error.response?.data.error.errorDesc}`,
        );
      } else {
        reportDtCustomError('GenericHandledError', JSON.stringify(error));
      }

      handleHTTPError(error);
      toggleLoading(false);
    }
  };

  // istanbul ignore next
  const handleAuthScope = async () => {
    try {
      const response = await AuthAPI.getSessionInfo();

      if (response?.status === 200) {
        const { result } = response.data;
        saveAuthType(loginType === 'sp' ? 'singpass' : 'corppass');
        LocalStorageService.setAuth(JSON.stringify(result));
        flushSync(() => {
          setCookie('sessionType', 'authed', { path: '/' });
          setCookie('isSessionActive', 'true', { path: '/' });
          authenticate();
        });
        handleNavigate();

        if (window.dtrum) {
          window.dtrum.identifyUser(maskNRIC(result.user?.uin));
        }
      }
    } catch (error) {
      console.error(error);

      // istanbul ignore next
      if (axios.isAxiosError(error)) {
        reportDtCustomError(
          'AuthFailureError',
          'Session info API failed.',
          `${error.response?.status}: ${error.response?.data.error.errorDesc}`,
        );
      } else {
        reportDtCustomError('GenericHandledError', JSON.stringify(error));
      }

      handleHTTPError(error);
    } finally {
      toggleLoading(false);
    }
  };

  // istanbul ignore next
  const handleNavigate = () => {
    const urlPriorToLogin = LocalStorageService.getUrlPriorToLogin();

    // Check if auth type is singpass. If not, proceed to check indicators
    if (loginType === 'sp') {
      navigate(urlPriorToLogin ?? '/dashboard');
      toggleLoading(false);
      LocalStorageService.clearUrlPriorToLogin();
      return;
    }

    const hasYIndicator = checkIndicator();

    if (hasYIndicator) {
      navigate('/profile-selection');
      return;
    }

    toggleEntityChange('false');
    navigate(urlPriorToLogin ?? '/dashboard');
    toggleLoading(false);
    LocalStorageService.clearUrlPriorToLogin();
  };

  return <></>;
};

export default Authenticate;
